import React, { useState, useEffect, useRef, useContext } from 'react';
import { Session } from '../../contexts/sessionContext';

import styled from 'styled-components';
import axios from 'axios';
import { Row, Flex, Box, Card, Button, Text } from '../../design-system';
import { Form } from 'react-final-form';
import Alert from '../../helpers/alert';
import { required, composeValidators, emailVal } from '../../helpers/utils';
import FieldColumn from '../../components/FieldColumn';
import { ERR_MSG, SUCCESS_MSG } from '../../globalConstants';
import { ACTIONS } from '../../contexts/sessionContext';
import GoogleContactsModal from './GoogleContactsModal';
import {
  getGoogleConnectionStatus,
  initiateGoogleAuth,
  disconnectGoogle,
} from '../../helpers/googleContacts';
import ForgotPassword from '../Password/ForgotPassword';

const TIMEZONE_OPTIONS = [
  { label: 'America/New_York', value: 'America/New_York' },
  { label: 'America/Chicago', value: 'America/Chicago' },
  { label: 'America/Denver', value: 'America/Denver' },
  { label: 'America/Los_Angeles', value: 'America/Los_Angeles' },
  { label: 'America/Anchorage', value: 'America/Anchorage' },
  { label: 'Pacific/Honolulu', value: 'Pacific/Honolulu' },
];

const StyledButton = styled(Button)`
  width: 9.375rem;
`;

const ResetStyledButton = styled(Button)`
  width: 9.375rem;
  background-color: white;
  color: red;

  &:hover {
    background-color: #f0f0f0;
  }
`;
const CardHeader = styled(Card.Header)`
  font-size: 20px;
`;

const InputReadOnlyLabel = styled.p`
  margin: 5px;
`;

const InputReadOnly = styled.input`
  border: 1px solid #b5bac8;
  height: 48px;
  display: block;
  outline: none;
  width: 100%;
  padding: 8px;
  padding-right: 24px;
  border-radius: 8px;
  -webkit-transition: 250ms all;
  transition: 250ms all;
  -webkit-appearance: none;
  font-family: inherit;
  font-size: 16px;
  color: #121212;
  box-sizing: border-box;
`;

interface ProfileProps {
  user: any;
  dispatch: (action: any) => void;
}

const Profile = ({ user, dispatch }: ProfileProps) => {
  const [googleIntegration, setGoogleIntegration] = useState<any>(null);
  const [isGoogleModalOpen, setIsGoogleModalOpen] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const isMountedRef = useRef(true);

  const { logout } = useContext(Session);

  useEffect(() => {
    const initializeComponent = async () => {
      if (isMountedRef.current) {
        await checkGoogleConnection();

        // Check for success/error messages from OAuth callback
        const urlParams = new URLSearchParams(window.location.search);
        const success = urlParams.get('success');
        const error = urlParams.get('error');

        if (success && isMountedRef.current) {
          Alert.success(success);
          // Clean up URL
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname,
          );
        }
        if (error && isMountedRef.current) {
          Alert.error(error);
          // Clean up URL
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname,
          );
        }
      }
    };

    initializeComponent();

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const checkGoogleConnection = async () => {
    try {
      const status = await getGoogleConnectionStatus();
      if (status && isMountedRef.current) {
        setGoogleIntegration(status.connected ? status.integration : null);
      }
    } catch (error) {
      console.error('Failed to check Google connection:', error);
    }
  };

  const onSubmit = async (values: any) => {
    try {
      const { data } = await axios.put(`/user/${user.id}`, values);
      dispatch({
        type: ACTIONS.SET_USER,
        value: {
          ...user,
          name: data.name,
          email: data.email,
          timezone: data.timezone,
        },
      });
      Alert.success(SUCCESS_MSG);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };
  const [resetLoading, setResetLoading] = React.useState(false);
  const [isValidEmail, setIsValidEmail] = useState(false);

  const handleResetPassword = async () => {
    setResetLoading(true);
    try {
      await logout().then(async () => {
        await axios
          .post('/auth/password/email', {
            email: user.email,
          })
          .then((response) => {
            if (response.status === 200) {
              Alert.success('Email reset link has been sent');
              setResetLoading(false);
            }
          });
      });
    } catch (error: any) {
      Alert.error(ERR_MSG);
    }
  };

  const handleGoogleConnect = async () => {
    setGoogleLoading(true);
    try {
      const authUrl = await initiateGoogleAuth();
      window.location.href = authUrl;
    } catch (error) {
      Alert.error(ERR_MSG);
      console.error('Failed to initiate Google auth:', error);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleDisconnect = async () => {
    setGoogleLoading(true);
    try {
      await disconnectGoogle();
      setGoogleIntegration(null);
      Alert.success('Google account disconnected successfully');
    } catch (error) {
      Alert.error(ERR_MSG);
      console.error('Failed to disconnect Google:', error);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleSyncComplete = () => {
    checkGoogleConnection();
  };

  return (
    <Flex flexDirection="column">
      <Card p={2} style={{ marginBottom: '10px' }}>
        <Card.Header fontSize={24}>{user.name}</Card.Header>
        <Card.Body>
          <Form
            initialValues={{
              name: user.name,
              email: user.email,
              timezone: user.timezone || 'America/Chicago',
            }}
            onSubmit={onSubmit}
            render={({ handleSubmit, submitting, pristine }) => {
              return (
                <Card>
                  <CardHeader>Profile</CardHeader>
                  <form onSubmit={handleSubmit} style={{ padding: '15px' }}>
                    <Row>
                      <FieldColumn
                        name="name"
                        label="Name"
                        validate={required}
                        width="50%"
                      />

                      <FieldColumn
                        name="email"
                        label="Email"
                        validate={composeValidators(emailVal, required)}
                        width="50%"
                      />
                    </Row>
                    <Row>
                      <FieldColumn
                        name="timezone"
                        label="Timezone"
                        component="select"
                        options={TIMEZONE_OPTIONS}
                        width="50%"
                      />
                    </Row>
                    <div className="flex items-center justify-between">
                      <ResetStyledButton
                        type="button"
                        onClick={handleResetPassword}
                        disabled={resetLoading}
                      >
                        {resetLoading ? 'Sending...' : 'Reset Password'}
                      </ResetStyledButton>
                      <StyledButton
                        disabled={submitting || pristine}
                        aria-label="Save"
                        type="submit"
                      >
                        Save
                      </StyledButton>
                    </div>
                  </form>
                </Card>
              );
            }}
          />
        </Card.Body>
      </Card>
      <Card p={2} style={{ marginBottom: '10px' }}>
        <CardHeader>Calendar Sync</CardHeader>
        <Form
          onSubmit={() => {}}
          render={({ handleSubmit, submitting, pristine }) => {
            return (
              <form style={{ padding: '5px 15px 35px' }}>
                <div style={{ marginBottom: '20px' }}>
                  <InputReadOnlyLabel>All Deadlines</InputReadOnlyLabel>
                  <Flex alignItems="center">
                    <InputReadOnly
                      readOnly
                      name="deadlines_all"
                      width="100%"
                      value={`webcal://${location.host}/calendar/ics/${user.calendar_token}/all/export/`}
                    />
                    <StyledButton
                      style={{ marginLeft: '10px', padding: '22px' }}
                      type="button"
                      onClick={() => {
                        window.open(
                          `${location.protocol}//${location.host}/calendar/download/${user.calendar_token}/all`,
                          '_blank',
                        );
                      }}
                    >
                      Download
                    </StyledButton>
                    <StyledButton
                      style={{ marginLeft: '10px', padding: '22px' }}
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `webcal://${location.host}/calendar/ics/${user.calendar_token}/all/export/`,
                        );
                        Alert.success('Copied to Clipboard');
                      }}
                    >
                      Copy
                    </StyledButton>
                  </Flex>
                </div>
                <div>
                  <InputReadOnlyLabel>My Deadlines</InputReadOnlyLabel>
                  <Flex alignItems="center">
                    <InputReadOnly
                      readOnly
                      name="deadlines_my"
                      width="100%"
                      value={`webcal://${location.host}/calendar/ics/${user.calendar_token}/my/export/`}
                    />
                    <StyledButton
                      style={{ marginLeft: '10px', padding: '22px' }}
                      type="button"
                      onClick={() => {
                        window.open(
                          `${location.protocol}//${location.host}/calendar/download/${user.calendar_token}/my`,
                          '_blank',
                        );
                      }}
                    >
                      Download
                    </StyledButton>
                    <StyledButton
                      style={{ marginLeft: '10px', padding: '22px' }}
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `webcal://${location.host}/calendar/ics/${user.calendar_token}/my/export/`,
                        );
                        Alert.success('Copied to Clipboard');
                      }}
                    >
                      Copy
                    </StyledButton>
                  </Flex>
                </div>
                <h3 style={{ marginTop: '30px', marginBottom: '10px' }}>
                  Instructions
                </h3>
                <ul style={{ padding: '0 20px' }}>
                  <li style={{ marginBottom: '5px' }}>
                    <a
                      target="_blank"
                      href="https://support.google.com/calendar/answer/37118?hl=en&co=GENIE.Platform%3DDesktop"
                      rel="noreferrer"
                    >
                      Google Calendar
                    </a>
                  </li>
                  <li style={{ marginBottom: '5px' }}>
                    <a
                      target="_blank"
                      href="https://support.microsoft.com/en-us/office/import-calendars-into-outlook-8e8364e1-400e-4c0f-a573-fe76b5a2d379"
                      rel="noreferrer"
                    >
                      Microsoft Outlook
                    </a>
                  </li>
                </ul>
              </form>
            );
          }}
        />
      </Card>
      <Card>
        <CardHeader>Google Contacts Sync</CardHeader>
        <Card.Body>
          {googleIntegration ? (
            <div style={{ padding: '15px 0px' }}>
              <Flex gap={12}>
                <StyledButton
                  onClick={() => setIsGoogleModalOpen(true)}
                  disabled={googleLoading}
                >
                  Import Contacts
                </StyledButton>
                <Button
                  variant="secondary"
                  onClick={handleGoogleDisconnect}
                  loading={googleLoading}
                  style={{ marginLeft: '10px', width: '9.375rem' }}
                >
                  Disconnect
                </Button>
              </Flex>
              <div style={{ marginTop: '10px', opacity: 0.5 }}>
                <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                  Connected to Google{' '}
                  {new Date(
                    googleIntegration.connected_at,
                  ).toLocaleDateString()}
                </p>
              </div>
            </div>
          ) : (
            <div style={{ padding: '15px 0px' }}>
              <div style={{ marginBottom: '20px' }}>
                <Text fontSize={16} fontWeight={600} mb={8}>
                  Connect Google Account.
                </Text>
                <Text
                  fontSize={14}
                  color="#6c757d"
                  style={{ marginLeft: '5px' }}
                >
                  Import your Google contacts to Grant Holster.
                </Text>
              </div>
              <StyledButton
                onClick={handleGoogleConnect}
                loading={googleLoading}
              >
                Connect Google
              </StyledButton>
            </div>
          )}
        </Card.Body>
      </Card>

      <GoogleContactsModal
        isOpen={isGoogleModalOpen}
        onClose={() => setIsGoogleModalOpen(false)}
        onSyncComplete={handleGoogleSyncComplete}
      />
    </Flex>
  );
};

export default Profile;
